import type { FC } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import { css } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import Image from '@atlaskit/image';
import { ModalTransition } from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/standard-button';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import CheckIcon from '@atlaskit/icon/core/check-mark';
import AutomationIcon from '@atlaskit/icon/core/automation';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';

import {
	withIsSiteAdmin,
	type WithIsSiteAdminProps,
} from '@confluence/change-edition/entry-points/higher-order.ts';
import { GradientCTAButton } from '@confluence/change-edition/entry-points/GradientCTAButton.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import leftIllustration from './images/Illustration.svg';
import type { ListItemProps } from './ListItem.tsx';
import { ListItem } from './ListItem.tsx';
import { PremiumFeatureKey } from './useTrackPremiumFeatureUsage.ts';
import { i18n as i18nPto } from './PremiumTrialOnboardingModal.tsx';

const i18n = defineMessages({
	modalBodyText: {
		id: 'premium-onboarding.modal.body.text.pto.rt',
		description:
			'The text is shown as the supporting body copy under the main heading in the Premium trial reverse trial onboarding modal when a user opens it. It tells them how many of the most popular Premium features they can start exploring, adjusting the wording based on the number of features. The placeholder n is substituted with the number of available features as a numeral, resolving to “1 most popular feature” when there is one and “# most popular features” otherwise. Explains how to begin exploring key features in this flow.',
		defaultMessage:
			'Get started by exploring our {n, plural, one {# most popular feature} other {# most popular features}}.',
	},
	additionalFeaturesTitle: {
		id: 'premium-onboarding.title.additional.features',
		description:
			'The text is shown as the small heading above the additional features list in the Premium trial reverse trial onboarding modal when extra benefits are listed. It introduces the short list of other included Premium capabilities below. There are no placeholders in this message. Explains that more benefits follow.',
		defaultMessage: 'Plus:',
	},
	atlassianAIAdditionalFeatureText: {
		id: 'premium-onboarding.text.additional.feature.atlassian.ai',
		description:
			'The text is shown as one of the bullet-style additional feature labels in the Premium trial reverse trial onboarding modal when listing extra benefits. It names Atlassian Intelligence as an included Premium capability. There are no placeholders in this message. Explains the name of the AI feature being highlighted.',
		defaultMessage: 'Atlassian Intelligence (AI)',
	},
	whiteboardAdditionalFeatureText: {
		id: 'premium-onboarding.text.additional.feature.whiteboard',
		description:
			'The text is shown as one of the bullet-style additional feature labels in the Premium trial reverse trial onboarding modal when listing extra benefits. It describes that Premium includes unlimited whiteboards and storage. There are no placeholders in this message. Explains this combined storage and whiteboards benefit.',
		defaultMessage: 'Unlimited whiteboards and storage',
	},
	bulkContentToolsAdditionalFeatureText: {
		id: 'premium-onboarding.text.additional.feature.bulk.content.tools',
		description:
			'The text is shown as one of the bullet-style additional feature labels in the Premium trial reverse trial onboarding modal when listing extra benefits. It names bulk content management tools as an included Premium capability. There are no placeholders in this message. Explains the extra content management feature being highlighted.',
		defaultMessage: 'Bulk content management tools',
	},
	advancePermissionsAdditionalFeatureText: {
		id: 'premium-onboarding.text.additional.feature.advance.permissions',
		description:
			'The text is shown as one of the bullet-style additional feature labels in the Premium trial reverse trial onboarding modal when listing extra benefits. It names advanced permissions as an included Premium capability. There are no placeholders in this message. Explains the extra access control feature being highlighted.',
		defaultMessage: 'Advanced permissions',
	},
});

const imageContainerStyles = xcss({
	width: '145',
	minInlineSize: 'max-content',
});

const textAreaStyles = xcss({
	width: '567px',
});

const textAreaBodyStyles = xcss({
	paddingInline: 'space.500',
	paddingBottom: 'space.300',
});

const closeButtonStyles = xcss({
	paddingRight: 'space.050',
	paddingTop: 'space.200',
	textAlign: 'right',
	minInlineSize: 'max-content',
});

const listItemWrapperStyles = xcss({
	marginBlock: 'space.300',
});

const buttonWrapperStyles = xcss({
	width: '100%',
	paddingTop: 'space.300',
	paddingBottom: 'space.100',
	textAlign: 'right',
});

const imageStyles = css({
	objectFit: 'cover',
	minHeight: '100%',
	width: '168px',
	height: '400px',
});

type ModalProps = {
	stop?: () => void;
} & WithIsSiteAdminProps;

const ModalComponent: FC<ModalProps> = ({ isSiteAdmin, stop }) => {
	const [isOpen, setIsOpen] = useState(true);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { Modal } = useChoreographerAssets();

	const sharedFeaturesMapper: Array<ListItemProps & { featureKey: PremiumFeatureKey }> = [
		{
			title: i18nPto.teamCalendarsTitle,
			bodyText: i18nPto.teamCalendarsBodyText,
			icon: CalendarIcon,
			featureKey: PremiumFeatureKey.TEAM_CALENDAR,
		},
		{
			title: i18nPto.automationTitle,
			bodyText: i18nPto.automationBodyText,
			icon: AutomationIcon,
			featureKey: PremiumFeatureKey.AUTOMATION,
		},
		{
			title: i18nPto.analyticsTitle,
			bodyText: i18nPto.analyticsBodyTextPTO2,
			icon: ChartTrendUpIcon,
			featureKey: PremiumFeatureKey.ANALYTICS,
		},
	];

	const closeModal = useCallback(
		async (actionSubjectId: any) => {
			actionSubjectId &&
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'clicked',
						actionSubject: 'button',
						actionSubjectId,
						source: 'premiumTrialOnboardingReverseTrialModal',
					},
				}).fire();
			setIsOpen(false);
			stop?.();
		},
		[createAnalyticsEvent, stop],
	);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'premiumTrialOnboardingReverseTrialModal',
				attributes: {
					isSiteAdmin,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, isSiteAdmin]);

	const additionalFeaturesList = [
		i18n.atlassianAIAdditionalFeatureText,
		i18n.whiteboardAdditionalFeatureText,
		i18n.bulkContentToolsAdditionalFeatureText,
		i18n.advancePermissionsAdditionalFeatureText,
	];

	return (
		<TransparentErrorBoundary attribution={Attribution.FLYWHEEL_ENGAGEMENT}>
			<ModalTransition>
				{isOpen && (
					<Modal onClose={closeModal} autoFocus={false} width={712} shouldScrollInViewport>
						<Inline alignBlock="stretch">
							<Box xcss={imageContainerStyles}>
								<Image
									src={leftIllustration}
									srcDark={leftIllustration}
									// TODO: Update the attribute below so it is translated.
									// eslint-disable-next-line no-restricted-syntax
									alt="Premium trial onboarding illustration"
									css={imageStyles}
								/>
							</Box>
							<Stack xcss={textAreaStyles}>
								<Box xcss={closeButtonStyles}>
									<Button
										appearance="link"
										onClick={() => closeModal('premiumTrialOnboardingReverseTrialModalCloseButton')}
									>
										<CrossIcon spacing="spacious" label="Close Modal" color={token('color.text')} />
									</Button>
								</Box>
								<Box xcss={textAreaBodyStyles}>
									<Stack space="space.100">
										<Heading size="medium" as="h1">
											<FormattedMessage {...i18nPto.modalTitle} />
										</Heading>
										<Text size="small" color="color.text.subtlest">
											<FormattedMessage
												{...i18n.modalBodyText}
												values={{
													n: sharedFeaturesMapper.length,
												}}
											/>
										</Text>
									</Stack>
									<Box xcss={listItemWrapperStyles}>
										{sharedFeaturesMapper.map(
											(
												feature: ListItemProps & {
													featureKey: PremiumFeatureKey;
												},
											) => (
												<ListItem
													key={feature.featureKey}
													bodyText={feature.bodyText}
													icon={feature.icon}
													title={feature.title}
												/>
											),
										)}
									</Box>
									<Text>
										<FormattedMessage {...i18n.additionalFeaturesTitle} />
									</Text>
									{additionalFeaturesList.map((feature, i) => (
										<Inline alignBlock="center" space="space.100" key={`additional-feature-${i}`}>
											<CheckIcon label="" />
											<Text>
												<FormattedMessage {...feature} />
											</Text>
										</Inline>
									))}
									<Box xcss={buttonWrapperStyles}>
										<GradientCTAButton
											onClick={() =>
												closeModal('premiumTrialOnboardingReverseTrialModalExploreFeatures')
											}
										>
											<FormattedMessage {...i18nPto.buttonText} />
										</GradientCTAButton>
									</Box>
								</Box>
							</Stack>
						</Inline>
					</Modal>
				)}
			</ModalTransition>
		</TransparentErrorBoundary>
	);
};

export const PremiumTrialOnboardingReverseTrialModal = withIsSiteAdmin(ModalComponent);
